import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { cn } from "@/core/utils/utils";
import { ChangeStepper } from "@/payroll/editor/change-stepper";
import {
  ARTIFACT_CONTROLS,
  PANE_BOTTOM_CLEARANCE,
} from "@/payroll/editor/constants";
import { PaneSearchControl } from "@/payroll/editor/pane-search-control";
import { Legend, markOf } from "@/payroll/editor/roster-marks";
import type { RosterView, RosterMember } from "@/payroll/editor/types";
import { usePaneSearch } from "@/payroll/editor/use-pane-search";
import { formatRosterValue } from "@/payroll/roster/utils";
import { RosterRow, HEAD, PINNED, EDGE } from "./roster-row";
export const RosterTable = ({ roster }: { roster: RosterView }) => {
  // Counted over the rows on screen rather than from the server's totals, so
  // the number beside a swatch is exactly how many dots of that colour there
  // are to find.
  const marks = roster.members.map(markOf);
  const mine = marks.filter((mark) => mark === "mine").length;
  const leaving = marks.filter((mark) => mark === "gone").length;
  const others = marks.filter((mark) => mark === "staged").length;
  // Stepping walks what this request did, when it did anything. A roster draft
  // can carry changes that were waiting there before the conversation started,
  // and sending the reader through those first buries the ones they asked for.
  // Anyone leaving is walked too, whoever staged it: it is the one change in
  // here that stops a person being paid.
  const walked = mine
    ? (m: RosterMember) => m.from_request || m.change === "removed"
    : (m: RosterMember) => !!m.change;
  // Counted over the rendered order rather than a separate list, so the nth
  // step and the nth row down the table are the same person.
  const steps: Record<string, number> = {};
  roster.members.forEach((member) => {
    if (walked(member)) steps[member.id] = Object.keys(steps).length;
  });
  const total = Object.keys(steps).length;

  const body = useRef<HTMLTableSectionElement>(null);
  const [step, setStep] = useState(0);
  // Name first, then every value on the row. An owner looks for a person, but
  // "who is on the Roswell team" is the same question asked of a column, and
  // the row already carries the words to answer it.
  const search = usePaneSearch(
    useMemo(
      () =>
        roster.members.map((member) => ({
          id: member.id,
          // The words on the row, formatted as the cells format them, old
          // values included. Searching the raw values looked for `true` where
          // the cell says Yes and for `2026-01-05` where it says Jan 5, and a
          // changed cell shows two values where this could only see one.
          text: [
            member.display_name,
            ...roster.fields.flatMap((field) => {
              const change = member.field_changes[field.key];
              return [
                formatRosterValue(member.values[field.key], field),
                ...(change
                  ? [
                      formatRosterValue(change.before, field),
                      formatRosterValue(change.after, field),
                    ]
                  : []),
              ];
            }),
          ]
            .map((value) => String(value ?? ""))
            .join(" "),
        })),
      [roster.members, roster.fields],
    ),
    body,
  );
  // Bring the active match into the middle, the same way a stepped change goes.
  useEffect(() => {
    if (!search.activeId) return;
    const node = body.current?.querySelector(
      `[data-member="${CSS.escape(search.activeId)}"]`,
    );
    const viewport = body.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!node || !viewport) return;
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  }, [search.activeId]);
  // Derived, not stored: the roster can be rewritten under the reader and leave
  // fewer changes than before, and a kept index would point past the end.
  const at = total ? ((step % total) + total) % total : 0;

  const goTo = (by: number) => {
    if (!total) return;
    const next = (((at + by) % total) + total) % total;
    setStep(next);
    const node = body.current?.querySelector(`[data-change="${next}"]`);
    const viewport = body.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!node || !viewport) return;
    // Scroll the one container by hand. `scrollIntoView` walks every scrollable
    // ancestor, so stepping through people also moved the page beside it.
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  };

  return (
    <section className="flex min-h-0 flex-col">
      <div
        className={cn(
          ARTIFACT_CONTROLS,
          "flex flex-wrap items-center gap-x-3 gap-y-1",
        )}
      >
        {!!total && (
          <ChangeStepper
            noun="change"
            label={total > 1 ? `Change ${at + 1} of ${total}` : undefined}
            onStep={goTo}
          />
        )}
        <span className="text-secondary-text">
          {roster.members.length} on the roster
        </span>
        {/* Two different facts, and conflating them is what made a roster
            draft five days older than the conversation read as something this
            request had done. Both publish together, so both are said. */}
        {mine ? (
          <Legend kind="mine">
            {`${mine} ${mine === 1 ? "person" : "people"} changed by this request`}
          </Legend>
        ) : (
          <span className="text-tertiary-text">
            This request changes nobody on the roster
          </span>
        )}
        {leaving > 0 && <Legend kind="gone">{`${leaving} leaving`}</Legend>}
        {others > 0 && (
          <Legend kind="staged">{`${others} already staged`}</Legend>
        )}
        <PaneSearchControl search={search} label="roster" className="ml-auto" />
      </div>
      {/* No scroll container of its own: it sits in the tab's ScrollArea, and
          a second one here fought it for the horizontal axis. */}
      {/* The table scrolls; the control row above it does not.

          The whole section used to sit inside one scroll area, so scrolling to
          row 200 of 320 took "320 on the roster" and the way through the
          changes off the top of the screen with it. Those are controls for the
          table, and controls that leave when you use the thing they control are
          no use. */}
      <ScrollArea className="min-h-0 flex-1">
        <table className="w-full border-collapse whitespace-nowrap text-md leading-5">
          <thead>
            {/* The column headers stay, not the count above them: forty rows
              down, which column a Yes sits in is the thing you need. Each cell
              carries its own classes rather than a `[&>th]` rule on the row,
              because that rule outranks a pinned cell's own z-index and let the
              scrolling columns paint over the pinned name. */}
            <tr className="text-left text-sm text-tertiary-text">
              {/* A reserved gutter, so a marked row does not shift the name
                column of every row beside it. */}
              <th
                className={cn(HEAD, PINNED, "left-0 z-20 w-6 min-w-6")}
                aria-label="Changed"
              />
              <th className={cn(HEAD, PINNED, EDGE, "left-6 z-20 pr-3")}>
                Name
              </th>
              {roster.fields.map((field) => (
                <th
                  key={field.key}
                  className={cn(HEAD, "pr-3", {
                    "text-stroke-secondary": !field.read_by_rules,
                  })}
                  title={
                    field.read_by_rules
                      ? "The pay rules read this"
                      : "On the roster, but the pay rules do not read it"
                  }
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={body}>
            {roster.members.map((member) => (
              <RosterRow
                key={member.id}
                member={member}
                fields={roster.fields}
                mark={steps[member.id]}
                here={steps[member.id] === at}
                found={search.matches.includes(member.id)}
                hit={search.activeId === member.id}
              />
            ))}
          </tbody>
        </table>
        {/* Scrolled with the rows, not held under the pane.

            The support launcher floats over the bottom right of the window, so
            the last names need somewhere to go to clear it. Put on the scroll
            container instead, this became a permanent band of empty white under
            a table that was cut off above it. */}
        <div aria-hidden className={PANE_BOTTOM_CLEARANCE} />
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </section>
  );
};
